import { DecimalPipe } from "@angular/common";
import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonStackComponent } from "@components/button-stack/button-stack.component";
import { InputComponent } from "@components/input/input.component";
import { MapPopupContentComponent } from "@components/map/components/widgets/map-popup/map-popup-content/map-popup-content.component";
import { isNil } from "lodash";
import { IMapComponent } from "~app/common/components/map/map.models";
import { IMapDrawLayer, IMapGeometryWicket } from "~app/common/components/map/map.settings";
import { MapCalculationService } from "~app/common/components/map/services/map-calculation.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { KontraktMediatorService } from "../../services/kontrakt-mediator.service";
import { VkKartaMediatorService } from "../../services/vk-karta-mediator.service";

export interface IVkKartaSkogsvardAtgardPopupData {
    canEdit: boolean;
    geometry: IMapGeometryWicket;
    layer: IMapDrawLayer;
    param: IVkKartaSkogsvardAtgardPopupParam;
}

export interface IVkKartaSkogsvardAtgardPopupParam {
    atgardId: string;
    kontraktId: string;
}

@Component({
    selector: "rask-vk-karta-skogsvard-popup",
    templateUrl: "./vk-karta-skogsvard-popup.component.html",
    styleUrls: ["./vk-karta-skogsvard-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapPopupContentComponent,
        InputComponent,
        ButtonStackComponent,
        ActionButtonComponent,
        DecimalPipe
    ]
})
export class VkKartaSkogsvardPopupComponent implements OnInit {
    map: IMapComponent;
    @Input() onClose: () => void;

    protected canEdit: boolean;
    protected figurAreal: number;
    protected skogsvardHeader: string;

    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly mapCalculationService = inject(MapCalculationService);
    private readonly vkKartaMediatorService = inject(VkKartaMediatorService);

    private drawLayer: IMapDrawLayer;
    private geometryWicket: IMapGeometryWicket;
    private param: IVkKartaSkogsvardAtgardPopupParam;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("data") set data(value: IVkKartaSkogsvardAtgardPopupData) {
        this.geometryWicket = value.geometry;
        this.drawLayer = value.layer;
        this.canEdit = value.canEdit;
        this.param = value.param;
    }

    get figurarealText() {
        return this.figurAreal ? "ha" : "Beräknar...";
    }

    abort = () => {
        if (this.geometryWicket?.persistedGeometry.isNew) {
            this.removeCurrentGraphic();
        } else {
            ObjectUtils.resetObjectFromObject(this.geometryWicket, this.geometryWicket.persistedGeometry);
            this.vkKartaMediatorService.updateGeometry(this.drawLayer.id,
                this.param.kontraktId,
                this.param.atgardId,
                this.geometryWicket.id,
                this.geometryWicket.wkt);
        }
        this.onClose();
    };

    addArea = () => {
        this.onClose();
        this.map.getSketch().add(this.drawLayer,
            this.geometryWicket, (g) => this.vkKartaMediatorService.updateGeometry(this.drawLayer.id, this.param.kontraktId, this.param.atgardId, g.id, g.wkt));
    };

    canSave = () => false;

    cut = () => {
        this.onClose();
        this.map.getSketch().cut(this.drawLayer,
            this.geometryWicket, (g) => this.vkKartaMediatorService.updateGeometry(this.drawLayer.id, this.param.kontraktId, this.param.atgardId, g.id, g.wkt));
    };

    edit = () => {
        this.onClose();
        this.map.getSketch().edit(this.drawLayer,
            this.geometryWicket, (g) => this.vkKartaMediatorService.updateGeometry(this.drawLayer.id, this.param.kontraktId, this.param.atgardId, g.id, g.wkt), true);
        this.map.getSketch().onAbort(() => {
            this.abort();
        });
    };

    hasPersistedData(): boolean {
        return !isNil(this.geometryWicket.persistedGeometry);
    }

    ngOnInit(): void {
        if (!this.hasPersistedData()) {
            this.geometryWicket.persistedGeometry = ObjectUtils.deepCopy(this.geometryWicket);
        }
        const atgard = this.kontraktMediatorService.atgarder.find(x => x.guid === this.param.atgardId);
        this.skogsvardHeader = atgard.atgardsgrupp;

        this.getAreal(this.geometryWicket.wkt, (areal) => {
            this.figurAreal = areal;
        });
    }

    save = () => true;

    protected closePopup = () => {
        this.onClose();
    };

    protected openCreateSkogsvardModal() {
        this.kontraktMediatorService.openOneOfSkogsvardCreateDialog(this.param.atgardId);
    }

    protected readonly removeCurrentGraphic = () => {
        this.vkKartaMediatorService.deleteGeometry(this.drawLayer.id, this.param.kontraktId, this.geometryWicket.id);
        this.onClose();
    };

    private getAreal(wkt: string, callback: (areal) => void) {
        this.mapCalculationService.getArea(wkt).subscribe(area => {
            const areal = this.mapCalculationService.calculateHektarFromArea(area, 2);
            callback(areal);
        });
    }
}
